// Added by Sejune Oh on 2020-03-20
// Copyright (c) Coreline Soft, All rights are reserved.
//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
import { FC, useState, useEffect } from 'react'
import useStore from '@/stores/global/hooks/useStore'

// API
import { reqGetTaskMonit } from '@/service/study/task.service'

// Helper
import LegacyEvtHandler from '@/helper/LegacyEvtHandler'
import { EVT_UPDATE_CURRENT_DB, EVT_STUDYLIST_SELECTION } from '@/helper/define/legacyEventDefine'
import { TaskStatusEnum, TaskMonitoringElement } from '@/helper/define/taskDefine'

// UI
import { TaskMonitoringBox, TaskMonitoringDetail } from '@/components/worklist/TaskMonitoring'

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// Interface
import { WebComponentProps } from '@/helper/registerWebComponent'
import { TaskMonitoringBoxStyleTypes } from './index'
import Logger from '@/helper/Logger'
import { USER_ACCESS_LEVEL } from '@/helper/define'

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
const TaskMonitoringBoxWrapper: FC<WebComponentProps> = ({
  customElementName,
  sendDataToCore,
  recvDataFromCore,
  classes,
  ...props
}) => {
  const { userAuth } = useStore()

  // ! State
  const [curProxyToken, setCurProxyToken] = useState<string | null>(null)
  const [onGettingCount, setGettingCountStatus] = useState(false)
  const [isAdminLevel, setIsAdminLevel] = useState(false)

  const [waitingCount, setWaitingCount] = useState(0)
  const [waitingTasks, setWaitingTasks] = useState<TaskMonitoringElement[]>([])
  const [runningCount, setRunningCount] = useState(0)
  const [runningTasks, setRunningTasks] = useState<TaskMonitoringElement[]>([])
  const [isDetailDialogOpened, setDetailDialogOpened] = useState(false)

  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  const getTasks = async (force = false) => {
    if (!curProxyToken) return
    if (!force && onGettingCount) return

    try {
      setGettingCountStatus(true)

      const {
        data: { items },
      } = await reqGetTaskMonit(curProxyToken!)
      const searchedTasks: TaskMonitoringElement[] = items.map(({ user_display_name, type, ...rest }) => {
        if (type === 'APS_RULE_TASK') {
          return { ...rest, type, user_display_name: `*${user_display_name}` }
        }

        return { ...rest, type, user_display_name }
      })
      const waitingList = searchedTasks.filter((item) => item.status === TaskStatusEnum.Waiting)
      const runningList = searchedTasks.filter((item) => item.status === TaskStatusEnum.Running || item.status === TaskStatusEnum.Invoking)
      const waitingCnt = waitingList.length
      const runningCnt = runningList.length
      setWaitingTasks(waitingList)
      setRunningTasks(runningList)
      setWaitingCount(waitingCnt)
      setRunningCount(runningCnt)
    } catch (e) {
      Logger.errLog('TASK_MONITORING_BOX.getTasks : Failed')
      console.log(e)
    } finally {
      setGettingCountStatus(false)
    }
  }

  const updateCurrentDbEvtHandler = (event: any) => {
    try {
      const { detail: proxyToken } = event
      setCurProxyToken(proxyToken)
      getTasks(true)
    } catch (e) {
      Logger.errLog('TASK_MONITORING_BOX.updateCurrentDbEvtHandler : Failed')
      console.log(e)
    }
  }

  const studylistSelectEvtHandler = (event: any) => getTasks()

  const handleBoxClick = () => {
    if (!curProxyToken) {
      Logger.errLog('TASK_MONITORING_BOX.handleBoxClick : Token is empty')
      return
    }

    if (runningCount === 0 && waitingCount === 0) return
    setDetailDialogOpened(true)
  }

  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  // ! Effect
  useEffect(() => {
    LegacyEvtHandler.addEvtListener(EVT_UPDATE_CURRENT_DB, updateCurrentDbEvtHandler)
    LegacyEvtHandler.addEvtListener(EVT_STUDYLIST_SELECTION, studylistSelectEvtHandler)
    /**
     * 최초에 한번 실행한 후 5초에 한번씩 Task 모니터링을 함.
     * getTasks가 끝나는 것을 기다린 뒤에 timeout setting
     * setInterval로 처리하면 이전 요청이 끝나든 그렇지 않든 무조건 재요청을 하기 때문.
     */

    getTasks()
    let timeoutId: NodeJS.Timeout = setTimeout(async function reqTasks() {
      await getTasks()
      timeoutId = setTimeout(reqTasks, 5000)
    }, 5000)

    const userLevel = Number(userAuth.userInfo.access_level)
    const ADMIN_LEVEL = USER_ACCESS_LEVEL.AccessLevelAdmin.enumValue
    if (userLevel >= ADMIN_LEVEL) {
      setIsAdminLevel(true)
    }

    return () => {
      LegacyEvtHandler.removeEvtListener(EVT_UPDATE_CURRENT_DB, updateCurrentDbEvtHandler)
      LegacyEvtHandler.removeEvtListener(EVT_STUDYLIST_SELECTION, studylistSelectEvtHandler)
      if (timeoutId) clearTimeout(timeoutId)
    }

    // eslint-disable-next-line
  }, [curProxyToken])

  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  // ! Render
  return (
    <>
      <div
        style={{
          boxSizing: 'border-box',
          backgroundColor: '#535353',
          overflowY: 'hidden',
          borderLeft: '2px solid #000',
          borderBottom: '1px solid #000',
          borderTop: '1px solid #000',
        }}
      >
        <TaskMonitoringBox
          waitingCount={waitingCount}
          runningCount={runningCount}
          classes={classes as TaskMonitoringBoxStyleTypes}
          onBoxClick={handleBoxClick}
        />
      </div>
      <TaskMonitoringDetail
        open={isDetailDialogOpened}
        waitingTasks={waitingTasks}
        runningTasks={runningTasks}
        proxyToken={curProxyToken}
        onCloseBtnClick={() => setDetailDialogOpened(false)}
        isAdminLevel={isAdminLevel}
      />
    </>
  )
}

export default TaskMonitoringBoxWrapper
